/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { TEAL, TEAL_LIGHT, getCurrentUserId } from '../opdAppointments/constants';

const HEADER_CELL_SX = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.92)',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  py: 1.3,
  whiteSpace: 'nowrap',
  borderBottom: 'none',
};

export default function OpdFeeConfig() {
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [feeConfig, setFeeConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registrationAmount, setRegistrationAmount] = useState('');
  const [defaultConsultationAmount, setDefaultConsultationAmount] = useState('');
  const [defaultRevisitAmount, setDefaultRevisitAmount] = useState('');
  const [doctorAmounts, setDoctorAmounts] = useState({});
  const [doctorRevisitAmounts, setDoctorRevisitAmounts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.contacts.getDoctorsList(), api.opdBilling.listFeeConfig()])
      .then(([doctorsResp, feeResp]) => {
        setDoctors(doctorsResp?.data || []);
        const rows = feeResp?.data || [];
        setFeeConfig(rows);

        const registration = rows.find(r => r.fee_type === 'REGISTRATION' && !r.irm_users_id);
        setRegistrationAmount(registration ? String(registration.amount) : '');

        const defaultConsultation = rows.find(r => r.fee_type === 'CONSULTATION' && !r.irm_users_id);
        setDefaultConsultationAmount(defaultConsultation ? String(defaultConsultation.amount) : '');

        const defaultRevisit = rows.find(r => r.fee_type === 'CONSULTATION_REVISIT' && !r.irm_users_id);
        setDefaultRevisitAmount(defaultRevisit ? String(defaultRevisit.amount) : '');

        const byDoctorId = {};
        rows
          .filter(r => r.fee_type === 'CONSULTATION' && r.irm_users_id)
          .forEach(r => { byDoctorId[r.irm_users_id] = String(r.amount); });
        setDoctorAmounts(byDoctorId);

        const revisitByDoctorId = {};
        rows
          .filter(r => r.fee_type === 'CONSULTATION_REVISIT' && r.irm_users_id)
          .forEach(r => { revisitByDoctorId[r.irm_users_id] = String(r.amount); });
        setDoctorRevisitAmounts(revisitByDoctorId);
      })
      .catch(() => {
        setDoctors([]);
        setFeeConfig([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key, feeType, doctorId, amountStr) => {
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount < 0) {
      Swal.fire({ icon: 'warning', title: 'Enter a valid amount' });
      return;
    }
    setSavingKey(key);
    try {
      await api.opdBilling.upsertFeeConfig({
        fee_type: feeType,
        irm_users_id: doctorId ?? undefined,
        amount,
        updated_by: getCurrentUserId(),
      });
      Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
      load();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/opd/billing')} sx={{ textTransform: 'none', color: TEAL }}>
            Back to OPD Billing
          </Button>
        </Box>

        <MDBox mb={3} display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${TEAL} 0%, #005f70 100%)`, boxShadow: `0 4px 14px ${TEAL}44`,
            }}
          >
            <SettingsIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <MDTypography variant="h4" fontWeight="bold" color="text">Fee Config</MDTypography>
            <MDTypography variant="body2" color="text" opacity={0.7}>Registration fee and per-doctor consultation fees</MDTypography>
          </Box>
        </MDBox>

        <Card>
          <MDBox p={3}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: TEAL }} />
              </Box>
            ) : (
              <Box sx={{ borderRadius: 3, border: '1px solid #eef2f5', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, #005f70 100%)` }}>
                        <TableCell sx={HEADER_CELL_SX}>Fee</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Amount (₹)</TableCell>
                        <TableCell sx={HEADER_CELL_SX} align="center">Actions</TableCell>
                      </TableRow>

                      <TableRow sx={{ background: '#fff', '&:hover': { background: TEAL_LIGHT } }}>
                        <TableCell sx={{ py: 1.3, fontWeight: 700 }}>Registration Fee (clinic-wide)</TableCell>
                        <TableCell sx={{ py: 1.3 }}>
                          <TextField size="small" type="number" value={registrationAmount} onChange={e => setRegistrationAmount(e.target.value)} sx={{ width: 140 }} />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.3 }}>
                          <MDButton
                            size="small"
                            variant="gradient"
                            color="logo"
                            disabled={savingKey === 'registration'}
                            onClick={() => save('registration', 'REGISTRATION', null, registrationAmount)}
                          >
                            {savingKey === 'registration' ? 'Saving…' : 'Save'}
                          </MDButton>
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ background: '#fafcff', '&:hover': { background: TEAL_LIGHT } }}>
                        <TableCell sx={{ py: 1.3, fontWeight: 700 }}>Consultation Fee (default, no doctor-specific fee set)</TableCell>
                        <TableCell sx={{ py: 1.3 }}>
                          <TextField size="small" type="number" value={defaultConsultationAmount} onChange={e => setDefaultConsultationAmount(e.target.value)} sx={{ width: 140 }} />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.3 }}>
                          <MDButton
                            size="small"
                            variant="gradient"
                            color="logo"
                            disabled={savingKey === 'default-consultation'}
                            onClick={() => save('default-consultation', 'CONSULTATION', null, defaultConsultationAmount)}
                          >
                            {savingKey === 'default-consultation' ? 'Saving…' : 'Save'}
                          </MDButton>
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ background: '#fff', '&:hover': { background: TEAL_LIGHT } }}>
                        <TableCell sx={{ py: 1.3, fontWeight: 700 }}>
                          Revisit Consultation Fee (default)
                          <Typography variant="caption" color="textSecondary" display="block">
                            Applies instead of the regular consultation fee when the registration fee is waived (within 3 months). Leave blank to keep charging the regular fee on revisits.
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.3 }}>
                          <TextField size="small" type="number" value={defaultRevisitAmount} onChange={e => setDefaultRevisitAmount(e.target.value)} sx={{ width: 140 }} />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.3 }}>
                          <MDButton
                            size="small"
                            variant="gradient"
                            color="logo"
                            disabled={savingKey === 'default-revisit'}
                            onClick={() => save('default-revisit', 'CONSULTATION_REVISIT', null, defaultRevisitAmount)}
                          >
                            {savingKey === 'default-revisit' ? 'Saving…' : 'Save'}
                          </MDButton>
                        </TableCell>
                      </TableRow>

                      {doctors.map((d, i) => (
                        <React.Fragment key={d.user_id}>
                          <TableRow sx={{ background: i % 2 === 0 ? '#fff' : '#fafcff', '&:hover': { background: TEAL_LIGHT } }}>
                            <TableCell sx={{ py: 1.3 }}>
                              {d.name} {d.occupation ? <Typography component="span" variant="caption" color="textSecondary">({d.occupation})</Typography> : null}
                              <Typography variant="caption" color="textSecondary" display="block">Consultation Fee</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <TextField
                                size="small"
                                type="number"
                                placeholder={defaultConsultationAmount || '0'}
                                value={doctorAmounts[d.user_id] ?? ''}
                                onChange={e => setDoctorAmounts(prev => ({ ...prev, [d.user_id]: e.target.value }))}
                                sx={{ width: 140 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.3 }}>
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="info"
                                disabled={savingKey === `doctor-${d.user_id}`}
                                onClick={() => save(`doctor-${d.user_id}`, 'CONSULTATION', d.user_id, doctorAmounts[d.user_id] ?? '')}
                              >
                                {savingKey === `doctor-${d.user_id}` ? 'Saving…' : 'Save'}
                              </MDButton>
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ background: i % 2 === 0 ? '#fafcff' : '#f5f8fa', '&:hover': { background: TEAL_LIGHT } }}>
                            <TableCell sx={{ py: 1.3, pl: 4 }}>
                              <Typography variant="caption" color="textSecondary">Revisit Consultation Fee (within 3 months)</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <TextField
                                size="small"
                                type="number"
                                placeholder={defaultRevisitAmount || 'same as consultation fee'}
                                value={doctorRevisitAmounts[d.user_id] ?? ''}
                                onChange={e => setDoctorRevisitAmounts(prev => ({ ...prev, [d.user_id]: e.target.value }))}
                                sx={{ width: 140 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.3 }}>
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="info"
                                disabled={savingKey === `doctor-revisit-${d.user_id}`}
                                onClick={() => save(`doctor-revisit-${d.user_id}`, 'CONSULTATION_REVISIT', d.user_id, doctorRevisitAmounts[d.user_id] ?? '')}
                              >
                                {savingKey === `doctor-revisit-${d.user_id}` ? 'Saving…' : 'Save'}
                              </MDButton>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
